import { useEffect, useState } from "react"
import { Plus, Search } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover"
import { MAX_PICKED_PRODUCTS } from "../types"

interface ProductHit {
  external_id: string
  name: string
  sku: string | null
}

interface Props {
  /** JSON search endpoint (`?q=` is appended); market and org-wide pages differ. */
  searchUrl: string
  picked: string[]
  onAdd: (externalId: string) => void
}

export default function ProductPicker({ searchUrl, picked, onAdd }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [hits, setHits] = useState<ProductHit[]>([])
  const [loading, setLoading] = useState(false)
  const atCap = picked.length >= MAX_PICKED_PRODUCTS

  useEffect(() => {
    if (!open) return
    setLoading(true)
    const controller = new AbortController()
    const timer = setTimeout(() => {
      const url = `${searchUrl}?q=${encodeURIComponent(query)}`
      fetch(url, { headers: { Accept: "application/json" }, signal: controller.signal })
        .then((r) => r.json())
        .then((body: { products: ProductHit[] }) => {
          setHits(body.products)
          setLoading(false)
        })
        .catch(() => {})
    }, 200)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [open, query, searchUrl])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Search size={14} className="text-muted-foreground" />
          Add products
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-2">
        <Input
          autoFocus
          placeholder="Search by name or SKU"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {atCap && (
          <div className="mt-2 px-2 text-xs text-muted-foreground">
            The list holds up to {MAX_PICKED_PRODUCTS} products. Remove one to add another.
          </div>
        )}
        <div className="mt-2 max-h-[320px] overflow-y-auto">
          {hits.length === 0 && (
            <div className="px-2 py-6 text-center text-xs text-muted-foreground">
              {loading ? "Searching…" : "No products match."}
            </div>
          )}
          {hits.map((p) => {
            const added = picked.includes(p.external_id)
            return (
              <div key={p.external_id} className="flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-accent">
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm" title={p.name}>{p.name}</span>
                  {p.sku && <span className="text-[11px] text-muted-foreground">{p.sku}</span>}
                </div>
                <Button
                  variant={added ? "ghost" : "outline"}
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs"
                  disabled={added || atCap}
                  onClick={() => onAdd(p.external_id)}
                >
                  {added ? "Added" : <><Plus size={12} /> Add</>}
                </Button>
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
